// Getting-started checklist (D2) — shown on the dashboard until the operator is
// clearly up and running (has a chit + members) or dismisses it. Completion is
// derived from me.usage so it ticks itself off as the operator works.

import { useState, type ReactElement } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { orpc } from '../lib/orpc.js';
import { use_language } from '../i18n/index.js';
import { Icon } from './Icon.js';

const DISMISS_KEY = 'chitos_onboarding_dismissed';

export function OnboardingChecklist(): ReactElement | null {
  const lang = use_language();
  const ta = lang === 'ta';
  const tf = ta ? 'tamil' : '';
  const [dismissed, set_dismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  const usage_q = useQuery({ queryKey: ['me-usage'], queryFn: () => orpc.me.usage({}) });
  const u = usage_q.data?.usage;
  if (dismissed || !u) return null;

  const has_chit = u.active_chits > 0;
  const has_members = u.members > 0;
  // Once they have a chit AND members they're running — stop nagging.
  if (has_chit && has_members) return null;

  const steps: Array<{ done: boolean; to: string; en: string; ta: string }> = [
    { done: has_chit, to: '/chits/new', en: 'Create your first chit', ta: 'உங்கள் முதல் சீட்டை உருவாக்குங்கள்' },
    { done: has_members, to: '/subscribers', en: 'Add members', ta: 'உறுப்பினர்களைச் சேர்க்கவும்' },
    { done: false, to: '/settings/channels', en: 'Connect your WhatsApp number', ta: 'உங்கள் WhatsApp எண்ணை இணைக்கவும்' },
    { done: false, to: '/help', en: 'Read the quick guide', ta: 'விரைவு வழிகாட்டியைப் படியுங்கள்' },
  ];
  const done_count = steps.filter((s) => s.done).length;

  function dismiss(): void {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* ignore */
    }
    set_dismissed(true);
  }

  return (
    <section className="mb-6 rounded-2xl border border-primary/20 bg-primary/5 p-5">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className={`text-base font-semibold text-primary-ink ${tf}`}>{ta ? 'தொடங்குதல்' : 'Getting started'}</h2>
          <p className={`text-xs text-ink-3 mt-0.5 ${tf}`}>
            {ta ? `${done_count}/${steps.length} முடிந்தது` : `${done_count} of ${steps.length} done`}
          </p>
        </div>
        <button type="button" onClick={dismiss} className={`text-xs text-ink-3 hover:text-ink-2 ${tf}`}>
          {ta ? 'மறை' : 'Dismiss'}
        </button>
      </div>
      <ul className="grid gap-2 sm:grid-cols-2">
        {steps.map((s) => (
          <li key={s.to}>
            <Link
              to={s.to}
              className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 text-sm transition no-underline ${
                s.done ? 'border-line bg-surface/60 text-ink-3' : 'border-line bg-surface hover:border-primary text-ink-2'
              } ${tf}`}
            >
              <span
                className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-white ${
                  s.done ? 'bg-primary' : 'bg-ink/15'
                }`}
              >
                {s.done && <Icon.check />}
              </span>
              <span className={s.done ? 'line-through' : ''}>{ta ? s.ta : s.en}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
